import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, MessageSquare } from "lucide-react";

const CommentModal = ({ isOpen, onClose, onSubmit, photo }) => {
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setIsSubmitting(true);
    await onSubmit(comment);
    setComment("");
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10001] flex items-end sm:items-center justify-center"
        style={{ touchAction: "none" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: "spring", duration: 0.3, bounce: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-gradient-to-br from-neutral-ivory via-white to-accent-rose/10 rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-lg sm:max-h-[85vh] flex flex-col relative border-2 border-accent-gold/20 overflow-hidden"
          style={{
            maxHeight:
              "calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          {/* Contenido con padding */}
          <div className="p-6 flex flex-col h-full overflow-hidden">
            {/* Botón de cerrar */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-6 flex-shrink-0">
              <div className="relative">
                {/* Anillo pulsante de fondo */}
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.1, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 bg-gradient-to-br from-red-500 to-pink-500 rounded-full blur-xl"
                />

                {/* Icono principal con animación */}
                <div className="relative bg-gradient-to-br from-red-500 to-pink-500 rounded-full p-3 shadow-lg">
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <MessageSquare className="w-6 h-6 text-white" />
                  </motion.div>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-script text-primary">
                  Deja tu Comentario
                </h3>
                <p className="text-sm text-gray-600">
                  Comparte tu opinión sobre esta foto
                </p>
              </div>
            </div>

            {/* Foto miniatura */}
            {photo && (
              <div className="mb-4 flex items-center gap-3 p-3 bg-white/60 rounded-xl border border-gray-200 flex-shrink-0">
                <img
                  src={photo.url}
                  alt="Foto"
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-700 truncate">
                    {photo.uploader}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(photo.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}

            {/* Formulario */}
            <form
              onSubmit={handleSubmit}
              className="flex-1 flex flex-col min-h-0 overflow-hidden"
            >
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Escribe tu comentario aquí..."
                className="w-full h-32 sm:h-40 p-4 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none resize-none mb-3 font-sans text-gray-700"
                maxLength={500}
                disabled={isSubmitting}
                autoFocus
              />

              <div className="flex items-center justify-between mb-3 flex-shrink-0">
                <span className="text-xs text-gray-500">
                  {comment.length}/500 caracteres
                </span>
              </div>

              {/* Botones */}
              <div className="flex gap-3 flex-shrink-0 pb-safe">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="flex-1 py-3 px-4 sm:px-6 bg-white text-gray-600 font-medium rounded-full border-2 border-gray-200 hover:border-gray-300 transition-all duration-300 disabled:opacity-50 text-sm sm:text-base"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!comment.trim() || isSubmitting}
                  className="flex-1 py-3 px-4 sm:px-6 bg-gradient-to-r from-primary to-secondary text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Comentar
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CommentModal;
